import { ISelectFormDataSet, TableProperty, TableType, businessForecastStatusType } from '@/coreDomainsModule/table';
import { ViewModel } from '.';
import { i18n } from '@/i18n';
import {
  DISPLAY_ANNOUNCE_INITIAL_LENGTH,
  FORECAST_LENGTH,
  INVALID_NUMBER,
  LEADTIME_MONTH_UNIT,
  LEADTIME_WEEK_UNIT,
} from '@/coreDomainsModule/constants';
import { splitNumberFormat } from '@/utils/display/numbers';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { StdInventoryIndex } from '@/coreDomainsModule/entities/inventory';
import { IAnnounce, businessForecastStatus } from '@/coreDomainsModule/entities/forecast';
import { IDate, IYearMonth } from '@/coreDomainsModule/date';
import { IArrival } from '@/coreDomainsModule/entities/arrival';
import { simpleCopy } from '@/utils/deepCopy';
import { getValueForAdditionalHeaders } from '@/corePagesModule/addVariableTableContent/domains/valueObjects/TableContentHeaders';
import { RECOMMENDED_ORDER_DATE_WHEN_NULL } from '@/recommendOrderModule/constants';
import { convertLeadTimeByUnit } from '@/coreDomainsModule/leadtime';
import { ApiBusinessFreeColumnSetting } from '@/coreInfrastructureModule/interfaces/ISettingRepository';

/**
 * テーブル表示で使用中の自由欄ヘッダー。
 * columnNo は集計・関連それぞれで 1 から採番されるため、target と組み合わせて一意にする。
 */
export type BusinessFreeColumnHeader = {
  target: 'aggregate' | 'base';
  columnNo: number;
  headerName: string;
};

/**
 * 商品テーブルデータのUI表示の際に使うロジックをまとめる
 */
export class TableContentViewModel extends ViewModel {
  public getDisplayStdInventory = (item: IBusiness, stdInventoryIndex?: StdInventoryIndex): any => {
    // TODO: 23/11/20 stdInventoryIndex単体で表示でいい気はしているが、仕様がふわふわしているので一応残しておく
    if (stdInventoryIndex) {
      let outputStr = '';
      if (stdInventoryIndex.min === -1 || stdInventoryIndex.min === 0 || stdInventoryIndex.min == null) return '-';
      const dateValue = stdInventoryIndex?.option?.unit;
      let dateStr = 'unknown';
      switch (dateValue) {
        case 'day':
          dateStr = i18n.t('days');
          break;
        case 'week':
          dateStr = i18n.t('weeks');
          break;
        case 'month':
          dateStr = i18n.t('months');
          break;
      }
      outputStr = stdInventoryIndex.min + ' - ' + stdInventoryIndex.max + ' ' + dateStr;
      return outputStr;
    }
    let outputStr = '';
    if (!item.stdInventoryIndices) return outputStr;
    for (const stdInventory of Object.keys(item.stdInventoryIndices)) {
      if (item.stdInventoryIndices[stdInventory].min === -1) return '-';
      const dateValue = item.stdInventoryIndices[stdInventory].option.unit;
      let dateStr = 'unknown';
      switch (dateValue) {
        case 'day':
          dateStr = i18n.t('days');
        case 'week':
          dateStr = i18n.t('weeks');
          break;
        case 'month':
          dateStr = i18n.t('months');
          break;
      }
      outputStr = item.stdInventoryIndices[stdInventory].min + ' - ' + item.stdInventoryIndices[stdInventory].max + ' ' + dateStr;
      break; // 暫定的に1つ目の標準在庫指数を出力する
    }
    return outputStr;
  };

  public getDisplayValue = (value: any, labelKey?: string): any => {
    // TODO: salesStatusだけ横着して場合分けしているのでなんとかしたい
    if (value == null) {
      return i18n.t('複数');
    } else if (labelKey === 'salesStatus') {
      return i18n.t(value);
    } else if (labelKey === 'businessNumber') {
      return String(value ?? '');
    } else {
      return splitNumberFormat(value);
    }
  };

  public getLeadTime = (leadTime: number): any => {
    return this.displayTableContent(leadTime, LEADTIME_MONTH_UNIT, LEADTIME_WEEK_UNIT);
  };

  public getDisplayInventory = (item: IBusiness): number | string => {
    return this.getDisplayValue(item?.inventory || '');
  };

  public getMOQ = (business: IBusiness): string => {
    if (!business.MOQ) return '';
    const displayMOQ = splitNumberFormat(business.MOQ);
    return business.unit ? `${displayMOQ} ${business.unit}` : `${displayMOQ}`;
  };

  /**
   * 取引の中で使用されているbusinessMemoのkeyを全て取得する
   * @param businesses 取引リスト
   * @returns businessMemoのkeyのリスト
   */
  public getUsingBusinessMemoKeys = (businesses: IBusiness[]): string[] => {
    const businessMemoKeysSet = new Set<string>();
    for (const business of businesses) {
      if (business.businessMemo) {
        for (const key of Object.keys(business.businessMemo)) {
          businessMemoKeysSet.add(key);
        }
      }
    }
    if (businessMemoKeysSet.size === 0) businessMemoKeysSet.add('顧客台帳ID'); // この時どうするの？？　→　とりあえず適当な文字で表示する
    // businessMemoはkey, valueで管理しており順番が保証されない。いちいち順番変わるのも不自然なのでソートして返す
    return Array.from(businessMemoKeysSet).sort();
  };

  /**
   * テーブル表示用に、businessMemoのkeyの数だけヘッダーを増やす
   * @param headers 元のヘッダー
   * @param businessMemoKeys businessMemoのkeyのリスト
   * @returns businessMemoのkeyを含めたヘッダーのリスト
   */
  public replacedHeadersBusinessMemoWithKeys = (headers: string[], businessMemoKeys: string[]): string[] => {
    return headers.flatMap((key) => (key === 'businessMemo' ? businessMemoKeys.map((_, i) => `businessMemo-${i + 1}`) : key));
  };

  /**
   * テーブル表示で使用中の自由欄ヘッダーを、target（集計→関連）と columnNo 昇順で重複なく取得する。
   * columnNo は集計・関連で別採番のため、target と組み合わせないと同名でない別列が1列にまとめられてしまう。
   * @param businesses 取引リスト
   * @returns target・columnNo 昇順の自由欄ヘッダーリスト（設定ページで保存済みのもののみ）
   */
  public getUsingBusinessFreeColumnHeaders = (businesses: IBusiness[]): BusinessFreeColumnHeader[] => {
    const headerMap = new Map<string, BusinessFreeColumnHeader>();
    for (const business of businesses) {
      for (const column of business.businessFreeColumnValues ?? []) {
        // 設定ページでユーザーが保存していない自動デフォルト項目は表示しない
        if (!column.isConfigured) continue;
        // target 未指定の旧データは columnNo の範囲でフォールバック（1-3=集計、4-6=関連）
        const target: 'aggregate' | 'base' = column.target ?? (column.columnNo <= 3 ? 'aggregate' : 'base');
        const key = `${target}-${column.columnNo}`;
        if (!headerMap.has(key)) {
          headerMap.set(key, { target, columnNo: column.columnNo, headerName: column.headerName });
        }
      }
    }
    return Array.from(headerMap.values()).sort((a, b) => {
      if (a.target !== b.target) return a.target === 'aggregate' ? -1 : 1;
      return a.columnNo - b.columnNo;
    });
  };

  /**
   * テーブル表示用に、使用中の自由欄ヘッダーの数だけ businessFreeColumnValues 列を展開する。
   * @param headers 元のヘッダー
   * @param businessFreeColumnHeaders 使用中の自由欄ヘッダーリスト
   * @returns 自由欄を展開したヘッダーのリスト
   */
  public replacedHeadersBusinessFreeColumns = (headers: string[], businessFreeColumnHeaders: BusinessFreeColumnHeader[]): string[] => {
    return headers.flatMap((key) =>
      key === 'businessFreeColumnValues' ? businessFreeColumnHeaders.map((header) => this.getBusinessFreeColumnHeaderKey(header)) : key
    );
  };

  public getBusinessFreeColumnHeaderKey = (header: BusinessFreeColumnHeader): string => {
    return `businessFreeColumnValues-${header.target}-${header.columnNo}`;
  };

  public getBusinessFreeColumnHeaderByKey = (
    key: string,
    businessFreeColumnHeaders: BusinessFreeColumnHeader[]
  ): BusinessFreeColumnHeader | undefined => {
    const [, target, columnNo] = key.split('-');
    if ((target !== 'aggregate' && target !== 'base') || columnNo === undefined) return undefined;
    const parsedColumnNo = Number(columnNo);
    if (!Number.isInteger(parsedColumnNo)) return undefined;

    return businessFreeColumnHeaders.find((header) => header.target === target && header.columnNo === parsedColumnNo);
  };

  // 発注推奨日をviewに表示する
  public getDisplayRecommendOrderDates = (recommend: IArrival[], index = 0): Date | string => {
    if (recommend.length === 0) return i18n.t('推奨発注なし');
    return this.getDisplayRecommendOrderDate(recommend[index]);
  };

  /**
   * 推奨発注日が存在するかどうかを判定する
   * @param recommend シミュレーションによる推奨内容
   */
  public existRecommend = (recommend: IArrival) => {
    return recommend && recommend.amount >= 0;
  };
  public getDisplayRecommendOrderDate = (recommend: IArrival): Date | string => {
    return this.getRecommendOrderDate(recommend) ?? i18n.t('推奨発注なし');
  };
  public getRecommendOrderDate = (recommend: IArrival): Date | undefined => {
    return !(
      recommend.year === RECOMMENDED_ORDER_DATE_WHEN_NULL.year &&
      recommend.month === RECOMMENDED_ORDER_DATE_WHEN_NULL.month &&
      recommend.day === RECOMMENDED_ORDER_DATE_WHEN_NULL.day
    )
      ? DateService.convertIDateToDate(recommend)
      : undefined;
  };
  public getDisplayRecommendOrderValue = (recommend: IArrival): any => {
    if (this.existRecommend(recommend)) {
      const target = recommend;
      return target.amount;
    } else {
      return i18n.t('推奨発注なし');
    }
  };
  public getDisplayOrderNote = (recommend: IArrival): string | undefined => {
    if (this.existRecommend(recommend)) {
      return recommend.memoText;
    }
    return undefined;
  };

  /**
   * テーブルに表示する推奨発注日を取得する
   * @param item 取引
   */
  public getDisplayRecommendOrderString = (item: IBusiness): string => {
    // 子取引は推奨発注日を表示しない
    if (item.virtualChild) return '';
    const displayDateString = this.getDisplayRecommendOrderDates(item.recommend);
    return typeof displayDateString === 'string' ? displayDateString : DateService.getDisplayYearMonthDay(displayDateString);
  };

  /**
   * 日付情報を表示する
   * 日付情報がfalsyな場合にはデフォルト値を出力する
   * デフォルト出力は設定できるようにしている
   * @param inputDateValue 日付情報
   * @param defaultDate デフォルト出力
   */
  public displayDateValue = (inputDateValue: Date | undefined, defaultDate?: Date): string => {
    // 日付情報の確認
    if (inputDateValue) {
      // 日付情報がある場合
      return DateService.getDisplayYearMonthDay(inputDateValue);
    } else {
      if (defaultDate) {
        // 入力日付情報が無くてもデフォルト出力が設定されていればそちらを出力する
        return DateService.getDisplayYearMonthDay(defaultDate);
      }
    }
    return '-';
  };

  /**
   * テーブルに表示する推奨発注量を取得する
   * @param item 取引
   */
  public getDisplayRecommendOrderAmountString = (item: IBusiness): string => {
    // 子取引は推奨発注量を表示しない
    if (item.virtualChild) return '';
    // 最初の推奨発注量を参照するようにしている
    const displayAmountString = this.getDisplayRecommendOrderValue(item.recommend[0]);
    if (typeof displayAmountString === 'string') return displayAmountString;
    return splitNumberFormat(displayAmountString) ?? i18n.t('推奨発注なし');
  };

  /**
   * 強調表示する推奨発注日かどうかを判定する
   * @param item 取引
   * @returns 強調表示する推奨発注日かどうか
   */
  public shouldHighlightRecommendOrderDate = (item: IBusiness): boolean => {
    // 子取引は推奨発注日は不要
    if (item.virtualChild) return false;
    const displayDateString = this.getDisplayRecommendOrderDates(item.recommend);
    if (typeof displayDateString === 'string') return false;
    // 今日以前の日付は迅速に確認必須なため、強調表示する
    return DateService.isBeforeToday(displayDateString);
  };

  /**
   * リードタイムを単位に応じた値に変換し、単位と合わせた文字列を返す
   * @param value リードタイムの値
   * @param [month] 月の単位
   * @param [week] 週の単位
   * @returns リードタイムの値と単位を合わせた文字列
   */
  public displayTableContent = (value: number, month: number = LEADTIME_MONTH_UNIT, week: number = LEADTIME_WEEK_UNIT): any => {
    if (!value || value == -1) return '-';

    // 一般的にweekとdayは決定されるのでvalue,monthのみ必須項目
    if (!value) return;
    switch (this.getLeadTimeUnit(value, month, week)) {
      case 'month':
        return i18n.t(`months`, { number: splitNumberFormat(value / month) });
      case 'week':
        return i18n.t(`weeks`, { number: splitNumberFormat(value / week) });
      case 'day':
        return i18n.t(`days`, { number: splitNumberFormat(value) });
    }
  };
  public getRecommendCrossDate = (recommend: IArrival): Date | undefined => {
    return recommend.crossDate ? DateService.convertIDateToDate(recommend.crossDate) : undefined;
  };

  public getDisplayFixOrder = (fixOrderDate: IDate | undefined): any => {
    if (fixOrderDate === undefined) return '';
    return this.getDisplayValue(this.getDisplayDate(fixOrderDate));
  };

  public getDisplayDate = (date: IDate): any => {
    return i18n.t('displayDate', { year: String(date.year), month: String(date.month), day: String(date.day) });
  };

  /**
   * リードタイムの単位名を計算して取得する
   * @param leadTime リードタイムの値
   * @param [month] 月の単位
   * @param [week] 週の単位
   * @returns リードタイムの単位名
   */
  public getLeadTimeUnit = (leadTime: number, month: number = LEADTIME_MONTH_UNIT, week: number = LEADTIME_WEEK_UNIT): string => {
    if (leadTime % month === 0) {
      return 'month';
    } else if (leadTime % week === 0) {
      return 'week';
    } else {
      return 'day';
    }
  };

  // TODO: これはここに置くべきなのか?
  // TODO: 内示配列をsplit展開してテーブル表示している部分を適宜、見つけたらこちらの処理に変更してください
  /**
   * 内示情報の入った連想配列から、今月(テーブルを表示した時)から向こう4ヶ月分の内示情報を取り出す
   * @param announceArr 内示情報の配列
   * @param targetMonthMinutes 指定する月数(初期値: 4ヶ月)
   */
  public fetchForecastSpecifiedMonthsMinutes = (
    announceArr: IAnnounce[] | undefined,
    targetMonthMinutes = DISPLAY_ANNOUNCE_INITIAL_LENGTH
  ): IAnnounce[] => {
    // 取引情報に内示情報がない場合はtargetMonthMinutesで初期化して返す
    if (!announceArr) {
      return new Array(targetMonthMinutes).fill(undefined);
    }
    // テーブル表示時の年月情報の取得
    // TODO: ここ、fileManagerMvpとかでも使っているので共通化できないか?
    const nowDate: Date = new Date();
    const nowYear: number = nowDate.getFullYear();
    // https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Date/getMonthを参照
    const nowMonth: number = nowDate.getMonth() + 1;
    // 今月を含めた向こう4ヶ月分の年月情報を入れる配列
    const targetYearMonthArr: string[] = [];
    // 今月を含めた向こう4ヶ月分の年月を「年_月」の形で配列を作成する
    for (let i = 0; i < targetMonthMinutes; i++) {
      let key = '';
      // 年月情報を配列に追加していく
      if (nowMonth + i > 12) {
        // 年跨ぎ対応
        key = String(nowYear + 1) + '_' + String((nowMonth + i) % 12);
      } else {
        key = String(nowYear) + '_' + String(nowMonth + i);
      }
      targetYearMonthArr.push(key);
    }
    // 作成した向こう4ヶ月分の年月情報を元に、内示情報から必要な情報を取り出す
    const outputAnnounce: IAnnounce[] = [];
    for (const targetKey of targetYearMonthArr) {
      const yearMonthInfo: IYearMonth = {
        year: Number(targetKey.split('_')[0]),
        month: Number(targetKey.split('_')[1]),
      };
      // announceから対象年月の内示情報があるか探す
      const index: number = announceArr.findIndex((item) => DateService.equalYearMonthInfo(item, yearMonthInfo));
      // 出力用の配列に必要な内示情報を追加していく
      if (index < 0) {
        // 対象年月の内示情報がない場合、数量を0として出力用配列に追加する
        outputAnnounce.push({
          year: Number(targetKey.split('_')[0]),
          month: Number(targetKey.split('_')[1]),
          amount: 0,
          manageInitiatedYearMonth: undefined,
          createAt: undefined,
          updateAt: undefined,
        });
      } else {
        // 対象年月の内示情報がある場合は、内示情報を出力用配列に追加する
        outputAnnounce.push(announceArr[index]);
      }
    }
    return outputAnnounce;
  };

  public getDisplayForecast = (item: IAnnounce): any => {
    return this.getDisplayValue(splitNumberFormat(item?.amount) || '-');
  };

  /**
   * ソート時に使用する、比較可能な推奨発注日の値を取得する。
   * 推奨発注が存在しない場合はundefinedを返す。
   * @param recommend シミュレーションによる推奨内容
   * @returns 推奨発注日の値
   */
  public getComparableRecommendOrderDate = (recommend: IArrival): number | undefined => {
    const displayDateString = this.getDisplayRecommendOrderDate(recommend);
    return typeof displayDateString === 'string' ? undefined : displayDateString.getTime();
  };

  public getDisplayRecommendCrossDates = (recommend: IArrival[], index = 0): Date | string => {
    if (!recommend || recommend.length < 1) return i18n.t('推奨発注なし');
    return this.getDisplayRecommendCrossDate(recommend?.[index]);
  };
  public getDisplayRecommendCrossDate = (recommend: IArrival): Date | string => {
    if (recommend.crossDate !== undefined) {
      const crossDate = recommend.crossDate;
      return DateService.convertIDateToDate(crossDate);
    } else {
      return i18n.t('推奨発注なし');
    }
  };

  /**
   * ソート時に使用する、比較可能な推奨発注量の値を取得する。
   * 推奨発注が存在しない場合はundefinedを返す。
   * @param recommend シミュレーションによる推奨内容
   * @returns 推奨発注量の値
   */
  public getComparableRecommendOrderAmount = (recommend: IArrival): number | undefined => {
    return this.getDisplayRecommendOrderValue(recommend);
  };

  /**
   * テーブルに表示する推奨納期を取得する
   * @param item 取引
   */
  public getDisplayRecommendCrossDateString = (item: IBusiness): string => {
    // 子取引は推奨納期を表示しない
    if (item.virtualChild) return '';
    const displayDateString = this.getDisplayRecommendCrossDates(item?.recommend);
    return typeof displayDateString === 'string' ? displayDateString : DateService.getDisplayMonthDay(displayDateString);
  };

  /**
   * ソート時に使用する、比較可能な推奨納期の値を取得する。
   * 推奨発注が存在しない場合はundefinedを返す。
   * @param recommend シミュレーションによる推奨内容
   * @returns 推奨納期の値
   */
  public getComparableRecommendCrossDate = (recommend: IArrival): number | undefined => {
    const displayDateString = this.getDisplayRecommendCrossDate(recommend);
    return typeof displayDateString === 'string' ? undefined : displayDateString.getTime();
  };

  /**
   * 数量データを文字列に変換して表示する
   * もらったnumber型の値が0やnullなどのfalsyな値だった場合'-'に置き直す
   * テーブル表示で頻繁に使用しそうだったので関数化している
   * @param value 数量データ
   */
  public displayFromNumberToString = (value: number | undefined): string => {
    if (!value) return '-';
    return String(value);
  };

  public getDisplayLeadTimeWithUnit = (leadTimeSet: ISelectFormDataSet): string => {
    // displayDataがundefinedの場合 || displayDataが0や-1などの場合 || selectedUnitにnumber型の何かしらが入っている場合(リードタイムの運用上はstringが入ってるはず)
    if (!leadTimeSet.displayData || leadTimeSet.displayData <= 0 || typeof leadTimeSet.selectedUnit !== 'string') return '-';
    let unitStr = '';
    switch (leadTimeSet.selectedUnit) {
      case 'day':
        unitStr = i18n.t('days');
        break;
      case 'week':
        unitStr = i18n.t('weeks');
        break;
      case 'month':
        unitStr = i18n.t('months');
        break;
      default:
        break;
    }
    if (unitStr === '') return '-';
    return convertLeadTimeByUnit(leadTimeSet.displayData, leadTimeSet.selectedUnit) + unitStr;
  };

  /**
   * リードタイムの単位に応じた値を計算して取得する
   * @param leadTime リードタイムの値
   * @param [month] 月の単位
   * @param [week] 週の単位
   * @returns 単位に合ったリードタイムの値
   */
  public getLeadTimeDisplayValue = (leadTime: number, month: number = LEADTIME_MONTH_UNIT, week: number = LEADTIME_WEEK_UNIT): number => {
    switch (this.getLeadTimeUnit(leadTime, month, week)) {
      case 'month':
        return leadTime / month;
      case 'week':
        return leadTime / week;
      case 'day':
        return leadTime;
    }
    return INVALID_NUMBER;
  };

  /**
   * リードタイムを適切な単位に変換し、表示用のオブジェクトに格納して返す
   * @param leadTime リードタイムの値
   * @returns リードタイム情報
   */
  public getLeadTimeFormDataSet = (leadTime: number): ISelectFormDataSet => {
    const unit = this.getLeadTimeUnit(leadTime);
    const displayData = this.getLeadTimeDisplayValue(leadTime);
    return { displayData, selectedUnit: unit };
  };
}

/**
 * テーブル表示に当たって、汎用的に使うロジックをまとめる（表示内容の制御等）
 */
export class TableCommonViewModel extends ViewModel {
  public getBusinessFreeColumnHeadersFromSetting = (setting: ApiBusinessFreeColumnSetting): BusinessFreeColumnHeader[] => {
    const aggregateHeaders = setting.aggregate
      .map((item, index) => ({ item, columnNo: index + 1 }))
      .filter(({ item }) => item.isConfigured)
      .map(({ item, columnNo }) => ({
        target: 'aggregate' as const,
        columnNo,
        headerName: item.headerName,
      }));

    const baseHeaders = setting.base
      .map((item, index) => ({ item, columnNo: index + 1 }))
      .filter(({ item }) => item.isConfigured)
      .map(({ item, columnNo }) => ({
        target: 'base' as const,
        columnNo,
        headerName: item.headerName,
      }));

    return [...aggregateHeaders, ...baseHeaders];
  };
  // mvp版：発注推奨で用いる単一要素テーブルのプロパティ(stdInventoryに関しては、特殊なのでここでは対応しない)
  public mvpSimpleTableDisplayContents = (type: string): boolean => {
    if (
      type === 'leadTime' ||
      type === 'supplierName' ||
      type === 'businessName' ||
      type === 'inventory' ||
      type === 'package' ||
      type === 'MOQ' ||
      type === 'warehouse'
    ) {
      return true;
    } else {
      return false;
    }
  };

  // １つしかチェックボックスが選択できないようにするプロパティ群
  public checkOnlySingleCheckboxTables = (tableType: TableType): boolean => {
    if (
      tableType === 'receive-cooperation' ||
      tableType === 'send-cooperation' ||
      tableType === 'unsupported' ||
      tableType === 'bom-parent' ||
      tableType === 'registration' ||
      tableType === 'organization-business'
    ) {
      return true;
    }
    return false;
  };

  // シミュレーションによる推奨内容
  public tableRecommendContents = (type: string): boolean => {
    if (type === 'recommendedOrderDate' || type === 'recommendedOrderAmount' || type === 'recommendedDeliveryTime') {
      return true;
    } else {
      return false;
    }
  };

  // 日付でソートを行うヘッダーの集合
  public sortableDateBusinessTableHeaders = (type: string): boolean => {
    if (type === 'recommendedOrderDate') {
      return true;
    } else {
      return false;
    }
  };

  /**
   * ステータスに応じてテーブルの要素として表示するアイコンの名前を返す
   * @param type 列のタイプ（発注ステータス/仕入先内示ステータス）
   * @param status 現在のステータス
   */
  public getIconNameForecastState = (type: businessForecastStatusType, status: businessForecastStatus): string => {
    switch (type) {
      case 'orderStatus':
        switch (status) {
          case 'orderCompleted':
          case 'completed':
            return 'check';
          default:
            return 'cautionCircle';
        }
      case 'supplierForecastStatus':
        switch (status) {
          case 'supplierForecastCompleted':
          case 'completed':
            return 'check';
          default:
            return 'cautionCircle';
        }
      default:
        return '';
    }
  };

  // TableHeadのまとめられそうなプロパティ群
  public commonTableHeaders = (type: string): boolean => {
    if (
      type === 'expansion' ||
      type === 'edit' ||
      type === 'orderConfirmation' ||
      type === 'confirmSupplier' ||
      type === 'forecastDetail' ||
      type === 'saleQuantity' ||
      type === 'orderStatus' ||
      type === 'supplierForecastStatus'
    ) {
      return true;
    } else {
      return false;
    }
  };

  // 内示のプロパティ群
  public announceContents = (type: string): boolean => {
    if (type.includes('announce-')) {
      return true;
    } else {
      return false;
    }
  };

  /**
   * 列のヘッダー名からステータスのタイプ（発注ステータス/仕入先内示ステータス）を取得する
   * @param type ヘッダー名
   * @returns 発注ステータス/仕入先内示ステータス
   */
  public getBusinessForecastStatusType = (type: string): businessForecastStatusType | undefined => {
    if (type !== 'orderStatus' && type !== 'supplierForecastStatus') {
      return undefined;
    }
    return type === 'orderStatus' ? 'orderStatus' : 'supplierForecastStatus';
  };

  public excludeDisplaySettingContents = ['announce-', 'businessId', 'inputAnnounce'];

  // まとめられそうなプロパティ群
  public commonTableDisplayContents = (type: string): boolean => {
    if (
      type === 'salesStatus' ||
      type === 'endUser' ||
      type === 'salesDestinationName' ||
      type === 'supplierName' ||
      type === 'senderName' ||
      type === 'deliveryDestinationName' ||
      type === 'businessName' ||
      type === 'note' ||
      type === 'unit' ||
      type === 'parentNote' ||
      type === 'customerName' ||
      type === 'warehouse' ||
      type === 'manufacture' ||
      type === 'businessNumber'
    ) {
      return true;
    } else {
      return false;
    }
  };

  // preferences.tsと同期されている必要がある(そのうち一括で管理したい)
  public displayHeaders: Record<TableType, Array<string>> = {
    default: [
      'expansion',
      'choice',
      'salesStatus',
      'po',
      'edit',
      'businessId',
      'orderConfirmation',
      'fixOrderDate',
      'recommendedOrderDate',
      'recommendedDeliveryTime',
      'endUser',
      'salesDestinationName',
      'supplierName',
      'deliveryDestinationName',
      'businessName',
      'note',
      'unit',
      'form',
      'leadTime',
      'MOQ',
      'stdInventoryIndex',
      'orderAmount',
      'deliveryDate',
      'confirmSupplier',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
      'inventory',
      'forecastDetail',
      'saleQuantity',
      'inputAnnounce',
      'copyBusiness',
    ],
    forecast: [
      // TODO: 一応書いてみたが調整が必要
      'expansion',
      'choice',
      'customerName',
      'supplierName',
      'businessName',
      'package',
      'unit',
      'warehouse',
      'endUser',
      'deliveryDestinationName',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
      'subcontractor',
      'secondSubcontractor',
      'manufacture',
    ],
    inventory: [
      'expansion',
      'choice',
      'status',
      'salesStatus',
      'po',
      'edit',
      'businessId',
      'orderConfirmation',
      'fixOrderDate',
      'recommendedOrderDate',
      'recommendedDeliveryTime',
      'endUser',
      'salesDestinationName',
      'supplierName',
      'deliveryDestinationName',
      'businessName',
      'note',
      'unit',
      'form',
      'leadTime',
      'MOQ',
      'stdInventoryIndex',
      'orderAmount',
      'deliveryDate',
      'confirmSupplier',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
      'inventory',
      'forecastDetail',
      'saleQuantity',
      'inputAnnounce',
      'copyBusiness',
    ],
    calendar: [
      'expansion',
      'choice',
      'salesStatus',
      'po',
      'edit',
      'businessId',
      'orderConfirmation',
      'fixOrderDate',
      'recommendedOrderDate',
      'recommendedDeliveryTime',
      'endUser',
      'salesDestinationName',
      'supplierName',
      'deliveryDestinationName',
      'businessName',
      'note',
      'unit',
      'form',
      'leadTime',
      'MOQ',
      'stdInventoryIndex',
      'orderAmount',
      'deliveryDate',
      'confirmSupplier',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
      'inventory',
      'forecastDetail',
      'saleQuantity',
      'inputAnnounce',
      'copyBusiness',
    ],
    trash: [
      'expansion',
      'choice',
      'salesStatus',
      'po',
      'edit',
      'businessId',
      'orderConfirmation',
      'fixOrderDate',
      'recommendedOrderDate',
      'recommendedDeliveryTime',
      'endUser',
      'salesDestinationName',
      'supplierName',
      'deliveryDestinationName',
      'businessName',
      'note',
      'unit',
      'form',
      'leadTime',
      'MOQ',
      'stdInventoryIndex',
      'orderAmount',
      'deliveryDate',
      'confirmSupplier',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
      'inventory',
      'forecastDetail',
      'saleQuantity',
      'inputAnnounce',
      'copyBusiness',
    ],
    'organization-business': [
      // TODO: 一応書いてみたが調整が必要
      'expansion', // 展開
      'edit',
      'choice', // 選択
      'salesStatus', // ステータス
      'customerName', // 売上先
      'supplierName', // 仕入先
      'businessNumberNamedByUser', // 品番
      'businessName', // 商品名
      'package', // 容量
      'unit', // 単位
      'endUser', // エンドユーザー
      'deliveryDestinationName', // 納入先
      'warehouse', // 倉庫
      'manufacture', // 製造社
      'note', // 商品詳細
    ],
    registration: [
      'expansion',
      'choice',
      'po',
      'edit',
      'businessId',
      'orderConfirmation',
      'fixOrderDate',
      'recommendedOrderDate',
      'recommendedDeliveryTime',
      'endUser',
      'salesDestinationName',
      'supplierName',
      'deliveryDestinationName',
      'businessName',
      'note',
      'unit',
      'form',
      'leadTime',
      'MOQ',
      'stdInventoryIndex',
      'orderAmount',
      'deliveryDate',
      'confirmSupplier',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
      'inventory',
      'forecastDetail',
      'saleQuantity',
      'inputAnnounce',
      'copyBusiness',
    ],
    'recommend-order': [
      'expansion',
      'choice',
      'salesStatus',
      'po',
      'edit',
      'businessId',
      'orderConfirmation',
      'fixOrderDate',
      'recommendedOrderDate',
      'recommendedDeliveryTime',
      'endUser',
      'salesDestinationName',
      'supplierName',
      'deliveryDestinationName',
      'businessName',
      'note',
      'unit',
      'form',
      'leadTime',
      'MOQ',
      'stdInventoryIndex',
      'orderAmount',
      'deliveryDate',
      'confirmSupplier',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
      'inventory',
      'forecastDetail',
      'saleQuantity',
      'inputAnnounce',
      'copyBusiness',
    ],
    'fix-order': [
      'expansion',
      'choice',
      'salesStatus',
      'po',
      'edit',
      'businessId',
      'orderConfirmation',
      'fixOrderDate',
      'recommendedOrderDate',
      'recommendedDeliveryTime',
      'endUser',
      'salesDestinationName',
      'supplierName',
      'deliveryDestinationName',
      'businessName',
      'note',
      'unit',
      'form',
      'leadTime',
      'MOQ',
      'stdInventoryIndex',
      'orderAmount',
      'deliveryDate',
      'confirmSupplier',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
      'inventory',
      'forecastDetail',
      'saleQuantity',
      'inputAnnounce',
      'copyBusiness',
    ],
    unsupported: [
      // TODO: 一応書いてみたが調整が必要
      'expansion', // 展開
      'choice', // 選択
      'salesStatus', // ステータス
      'customerName', // 売上先
      'supplierName', // 仕入先
      'businessNumberNamedByUser', // 品番
      'businessName', // 商品名
      'package', // 容量
      'unit', // 単位
      'endUser', // エンドユーザー
      'deliveryDestinationName', // 納入先
      'warehouse', // 倉庫
      'manufacture', // 製造社
      'note', // 商品詳細
    ],
    'receive-cooperation': [
      // TODO: 一応書いてみたが調整が必要
      'expansion', // 展開
      'choice', // 選択
      'salesStatus', // ステータス
      'senderName', // 連携依頼を送信した企業の名前
      'customerName', // 売上先
      'supplierName', // 仕入先
      'businessNumberNamedByUser', // 品番
      'businessName', // 商品名
      'package', // 容量
      'unit', // 単位
      'endUser', // エンドユーザー
      'deliveryDestinationName', // 納入先
      'warehouse', // 倉庫
      'manufacture', // 製造社
      'note', // 商品詳細
    ],
    'send-cooperation': [
      // TODO: 一応書いてみたが調整が必要
      'expansion', // 展開
      'choice', // 選択
      'salesStatus', // ステータス
      'customerName', // 売上先
      'supplierName', // 仕入先
      'businessNumberNamedByUser', // 品番
      'businessName', // 商品名
      'package', // 容量
      'unit', // 単位
      'endUser', // エンドユーザー
      'deliveryDestinationName', // 納入先
      'warehouse', // 倉庫
      'manufacture', // 製造社
      'note', // 商品詳細
    ],
    'rejected-business': [
      // TODO: 一応書いてみたが調整が必要
      'expansion',
      'choice',
      'salesStatus',
      'customerName',
      'supplierName',
      'deliveryDestinationName',
      'businessName',
      'package',
      'unit',
      'manufacture',
      'warehouse',
      'endUser',
    ],
    'receive-forecast': [
      // 客先内示
      // TODO: 一応書いてみたが調整が必要
      'expansion', // 展開
      'choice', // 選択
      'customerName', // 売上先
      'supplierName', // 仕入先
      'businessNumberNamedByUser', // 品番
      'businessName', // 商品名
      'package', // 容量
      'unit', // 単位
      'endUser', // エンドユーザー
      'deliveryDestinationName', // 納入先
      'warehouse', // 倉庫
      'note', // 商品詳細
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
      'subcontractor', // 外注先
      'secondSubcontractor', // 外注先の外注先
      // 'manufacture',
    ],
    'send-forecast': [
      // 仕入先内示
      // TODO: 一応書いてみたが調整が必要
      'expansion', // 展開
      'choice', // 選択
      'customerName', // 売上先
      'supplierName', // 仕入先
      'businessNumberNamedByUser', // 品番
      'businessName', // 商品名
      'package', // 容量
      'unit', // 単位
      'endUser', // エンドユーザー
      'deliveryDestinationName', // 納入先
      'warehouse', // 倉庫
      'note', // 商品詳細
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
      'subcontractor', // 外注先
      'secondSubcontractor', // 外注先の外注先
      // 'manufacture',
    ],
    'sales-collection': [
      // TODO: 一応書いてみたが調整が必要
      'expansion',
      'choice',
      'salesStatus',
      'customerName',
      'supplierName',
      'deliveryDestinationName',
      'businessName',
      'package',
      'unit',
      'manufacture',
      'warehouse',
      'endUser',
    ],
    'bom-parent': [
      'expansion',
      'choice',
      'salesStatus',
      'po',
      'edit',
      'businessId',
      'orderConfirmation',
      'fixOrderDate',
      'recommendedOrderDate',
      'recommendedDeliveryTime',
      'endUser',
      'salesDestinationName',
      'supplierName',
      'deliveryDestinationName',
      'businessName',
      'note',
      'unit',
      'form',
      'leadTime',
      'MOQ',
      'stdInventoryIndex',
      'orderAmount',
      'deliveryDate',
      'confirmSupplier',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
      'inventory',
      'forecastDetail',
      'saleQuantity',
      'inputAnnounce',
      'copyBusiness',
    ],
    'bom-parts': [
      'expansion',
      'choice',
      'salesStatus',
      'po',
      'edit',
      'businessId',
      'orderConfirmation',
      'fixOrderDate',
      'recommendedOrderDate',
      'recommendedDeliveryTime',
      'endUser',
      'salesDestinationName',
      'supplierName',
      'deliveryDestinationName',
      'businessName',
      'note',
      'unit',
      'form',
      'leadTime',
      'MOQ',
      'stdInventoryIndex',
      'orderAmount',
      'deliveryDate',
      'confirmSupplier',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
      'inventory',
      'forecastDetail',
      'saleQuantity',
      'inputAnnounce',
      'copyBusiness',
    ],
    bom: [
      'expansion',
      'choice',
      'parentBusinessNumber', // 構成商品の名称変更に合わせて、'親商品番号'の方がいいのでは？
      'parentBusinessName',
      'parentUnit',
      'lossRate',
      'safetyNumber',
      'factoryPlace',
      'manufacturingLine',
      'parentNote',
      'childBusinessNumber', // 日本語としておかしい気がする。'構成商品番号'の方がいいのでは？
      'childBusinessName',
      'childUnit',
      'partsWeight',
      'supplierName',
    ],
    baseTable: [
      'choice',
      'expansion',
      'status',
      'orderConfirmation',
      'recommendedOrderDate',
      'recommendedOrderAmount',
      'recommendedDeliveryTime',
      'warehouse',
      'supplierName',
      'businessNumber',
      'businessName',
      'package',
      'unit',
      'customerName',
      'salesDestinationName',
      'leadTime',
      'stdInventoryIndex',
      'MOQ',
      'businessMemo',
      'businessFreeColumnValues',
      'arrivals',
      'salesResults',
      'inventory',
      'executionDate',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
    ],
    confirmSupplierTable: [
      'warehouse',
      'supplierName',
      'businessNumber',
      'businessName',
      'package',
      'customerName',
      'salesDestinationName',
      'leadTime',
      'stdInventoryIndex',
      'arrivals',
      'salesResults',
      'inventory',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounce').map((x, i) => x + '-' + (i + 1)),
    ],
    supplierTable: [
      'choice',
      'expansion',
      'orderStatus',
      'supplierForecastStatus',
      'warehouse',
      'supplierName',
      'businessNumber',
      'businessName',
      'package',
      'unit',
      'customerName',
      'salesDestinationName',
      'leadTime',
      'stdInventoryIndex',
      'MOQ',
      'businessMemo',
      'businessFreeColumnValues',
      'arrivals',
      'salesResults',
      'inventory',
      ...new Array(FORECAST_LENGTH).fill('supplierAnnounce').map((x, i) => x + '-' + (i + 1)),
    ],
    firmOrderTable: [
      'choice',
      'orderDate',
      'aggregateBusinessId',
      'warehouseNumber',
      'warehouse',
      'supplierNumber',
      'supplierName',
      'manufacturer',
      'businessNumber',
      'businessName',
      'package',
      'unit',
      'OrderGroupName',
      'OrderInvoiceNumber',
      'OrderDestinationName',
      'OrderDesiredTimeOfArrival',
      'OrderNumber',
      'OrderDeleteButton',
      'OrderEstimateTimeOfArrival',
      'OrderAmount',
      'OrderRecommendStatus',
      'supplierForecastResponsibleUser',
      'OrderMemo',
      'OrderAlert',
      'OrderOutputDate',
      'OrderLastUpdatedBy',
      'OrderEditButton',
      'headerModify',
    ],
    notExportedOrderTable: [
      'choice',
      'orderDate',
      'aggregateBusinessId',
      'warehouseNumber',
      'warehouse',
      'supplierNumber',
      'supplierName',
      'manufacturer',
      'businessNumber',
      'businessName',
      'package',
      'unit',
      'OrderGroupName',
      'OrderInvoiceNumber',
      'OrderDestinationName',
      'OrderNumber',
      'OrderDesiredTimeOfArrival',
      'OrderEstimateTimeOfArrival',
      'OrderAmount',
      'OrderRecommendStatus',
      'supplierForecastResponsibleUser',
      'OrderMemo',
      'headerModify',
    ],
    simpleBusinessSummary: ['warehouse', 'supplierName', 'businessNumber', 'businessName', 'package', 'MOQ', 'inventory'],
    progressRateTableByRepPerson: ['choice', 'userName', 'updatedBusinessCount', 'totalBusinessCount', 'progressRate', 'latestUpdateAt'],
    progressRateTableBySupplier: ['supplierName', 'updatedBusinessCount', 'totalBusinessCount', 'progressRate', 'latestUpdateAt'],
    forecastForReceiptStaffFilterTable: [
      'choice',
      'ForecastForReceiptStaff',
      'ForecastForReceiptRecoverStatus',
      'ForecastForReceiptRecoverRate',
      'ForecastForReceiptLatestUpdateAt',
      'ForecastForReceiptEditOption',
    ],
    forecastForReceiptAllBusinessTable: [
      'choice',
      'ForecastForReceiptStaffFull',
      'ForecastForReceiptName',
      'ForecastForReceiptResponsibleUser',
      'supplierName',
      'businessNumber',
      'businessName',
      'package',
      'warehouse',
      'customerName',
      'salesDestinationName',
      'ForecastForReceiptReceiveDate',
      'SupplierForecastCreateStatus',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounceForReceipt').map((x, i) => x + '-' + (i + 1)),
    ],
    forecastForReceiptSearchResultTable: [
      'ForecastForReceiptStaff',
      'ForecastForReceiptName',
      'ForecastForReceiptKeyword',
      'ForecastForReceiptRecoverStatus',
      'ForecastForReceiptRecoverRate',
      'ForecastForReceiptLatestUpdateAt',
    ],
    customerForecastDiffLastMonth: [
      'choice',
      'ForecastForReceiptStaff',
      'supplierName',
      'businessNumber',
      'businessName',
      'package',
      'warehouse',
      'customerName',
      'salesDestinationName',
      'ForecastForReceiptReceiveDate',
      'SupplierForecastCreateStatus',
      ...new Array(FORECAST_LENGTH).fill('customerAnnounceForReceipt').map((x, i) => x + '-' + (i + 1)),
    ],
    inventoryControl: [
      'choice',
      'warehouseNumber',
      'warehouse',
      'supplierNumber',
      'supplier',
      'businessNumber',
      'businessName',
      'package',
      'unit',
      'customerNumber',
      'customerName',
      'salesDestinationNumber',
      'salesDestinationName',
      'forecastForReceiptName',
      'manufacturer',
      'endUser',
      'purchaseUnitPrice',
      'salesUnitPrice',
      'leadTime',
      'stdInventoryIndex',
      'MOQ',
      'orderPackage',
      'supplierForecastResponsibleUser',
      'customerForecastResponsibleUser',
      'updatedAt',
      'createdAt',
      'businessFreeColumnValues',
      'memo',
      'Arrivals',
      'orderConfirmation',
      'headerModify',
    ],
    inventoryControlWithOrder: [
      'choice',
      'warehouseNumber',
      'warehouse',
      'supplierNumber',
      'supplier',
      'businessNumber',
      'businessName',
      'package',
      'unit',
      'customerNumber',
      'customerName',
      'salesDestinationNumber',
      'salesDestinationName',
      'forecastForReceiptName',
      'manufacturer',
      'endUser',
      'purchaseUnitPrice',
      'salesUnitPrice',
      'leadTime',
      'stdInventoryIndex',
      'MOQ',
      'orderPackage',
      'supplierForecastResponsibleUser',
      'customerForecastResponsibleUser',
      'updatedAt',
      'createdAt',
      'businessFreeColumnValues',
      'memo',
      'Arrivals',
      'orderConfirmation',
      'headerModify',
    ],
    /**
     * - 内示 1ヶ月後
     * - 内示 2ヶ月後
     * - 内示 3ヶ月後
     * - 入力日
     * - 対象ファイル
     * - 置き場名
     * - 仕入先名
     * - 商品CD
     * - 商品名
     * - 容量
     * - 売り上げ先名
     * - 送り先名
     */
    unmanagedBusinessTypeAnnounce: [
      'choice',
      'customerAnnounce-1',
      'customerAnnounce-2',
      'customerAnnounce-3',
      'inputDate',
      'fileName',
      'warehouse',
      'supplierName',
      'businessNumberNamedByUser',
      'businessName',
      'package',
      'customerName',
      'deliveryDestinationName',
    ],
    /**
     * - 販売実績数量
     * - 入力日
     * - 対象ファイル
     * - 置き場名
     * - 仕入れ先名
     * - 商品CD
     * - 商品名
     * - 容量
     * - 売り上げ先名
     * - 送り先名
     * - 標準在庫指数
     */
    unmanagedBusinessTypeSalesResult: [
      'choice',
      'saleQuantity',
      'inputDate',
      'fileName',
      'warehouse',
      'supplierName',
      'businessNumberNamedByUser',
      'businessName',
      'package',
      'customerName',
      'deliveryDestinationName',
      'stdInventoryIndex',
    ],
    /**
     * - 在庫数量
     * - 入力日
     * - 対象ファイル
     * - 置き場名
     * - 仕入れ先名
     * - 商品CD
     * - 商品名
     * - 容量
     */
    unmanagedBusinessTypeInventory: [
      'choice',
      'inventory',
      'inputDate',
      'fileName',
      'warehouse',
      'supplierName',
      'businessNumberNamedByUser',
      'businessName',
      'package',
    ],
    /**
     * - 入荷数量
     * - 入力日
     * - 対象ファイル
     * - 置場名
     * - 仕入れ先名
     * - 商品CD
     * - 商品名
     * - 容量
     * - 売り上げ先名
     * - 送り先名
     */
    unmanagedBusinessTypeArrival: [
      'choice',
      'arrivalAmount',
      'inputDate',
      'fileName',
      'warehouse',
      'supplierName',
      'businessNumberNamedByUser',
      'businessName',
      'package',
      'customerName',
      'deliveryDestinationName',
    ],
    /**
     * - BusinessID
     * - 置き場
     * - 仕入先名
     * - 商品CD
     * - 商品名
     * - 容量
     */
    unmanagedBusinessTypeModal: [
      'businessId',
      'warehouseNumber',
      'warehouse',
      'supplierNumber',
      'supplierName',
      'businessNumberNamedByUser',
      'businessName',
      'package',
    ],
    /**
     * - BusinessID
     * - 置き場
     * - 仕入先名
     * - 商品CD
     * - 商品名
     * - 容量
     * - 売上先名
     * - 送り先名
     */
    unmanagedBusinessTypeModalWhenAggregateRow: [
      'businessId',
      'warehouseNumber',
      'warehouse',
      'supplierNumber',
      'supplierName',
      'businessNumberNamedByUser',
      'businessName',
      'package',
      'customerNumber',
      'customerName',
      'deliveryDestinationNumber',
      'deliveryDestinationName',
    ],
    supplierForecastAllBusinessTable: [
      'choice',
      'expansion',
      'supplierForecastStaff',
      'businessId',
      'supplierName',
      'businessNumber',
      'businessName',
      'package',
      'warehouse',
      'customerName',
      'salesDestinationName',
      'supplierForecastUpdateAt',
      ...new Array(FORECAST_LENGTH).fill('supplierAnnounce').map((x, i) => x + '-' + (i + 1)),
    ],
    orderManageTable: [
      'choice',
      // 'expansion', TODO: 展開機能が実装されてから
      'orderStatus',
      'orderDate',
      'orderAmount',
      'deliveryDate',
      'warehouse',
      'customerName',
      'salesDestinationName',
      'leadTime',
      'stdInventoryIndex',
    ],
    deletionBusinessTable: [
      'choice',
      // 'expansion', TODO: 展開機能が実装されてから
      'warehouseNumber',
      'warehouse',
      'supplierNumber',
      'supplierName',
      'businessNumber',
      'businessName',
      'package',
      'customerNumber',
      'customerName',
      'salesDestinationNumber',
      'salesDestinationName',
      'leadTime',
      'stdInventoryIndex',
      'businessUpdateAt',
    ],
    /**
     *
     */
    moveWarehouse: [
      'warehouse',
      'supplierName',
      'businessNumber',
      'businessName',
      'package',
      'differenceOccurrenceMonthYear',
      'differenceAmount',
    ],
    moveWarehouseWithChoice: [
      'choice',
      'warehouse',
      'supplierName',
      'businessNumber',
      'businessName',
      'package',
      'differenceOccurrenceMonthYear',
      'differenceAmount',
    ],
    editAggregateBusinessChild: [
      'businessId',
      'warehouse',
      'supplierName',
      'businessNumber',
      'businessName',
      'package',
      'customerName',
      'deliveryDestinationName',
    ],
    orderCalendar: [],
    forecastAccuracyTable: [],
    unmatchedOrder: [
      'checkbox',
      'actionType',
      'warehouseNumber',
      'warehouse',
      'supplierNumber',
      'supplierName',
      'businessNumber',
      'businessName',
      'package',
      'orderNumber',
      'createdOrderDate',
      'desiredDeliveryDate',
      'estimatedTimeOfArrival',
      'orderAmount',
      'orderStatus',
      'errorReason',
      'headerModify',
    ],
    // 少なくともEzTableを使用しているところはここで定義する必要ない
    inventoryDashboardStock: [],
    inventoryDashboardForecastAccuracy: [],
    inventoryDashboardAlert: [],
    inventoryDashboardByDimensionTurnover: [],
    inventoryDashboardByDimensionEndInventory: [],
    inventoryDashboardByDimensionArrival: [],
    inventoryDashboardByDimensionOrderAmount: [],
    inventoryDashboardByDimensionSupplierForecast: [],
    inventoryDashboardByDimensionSalesResult: [],
    inventoryDashboardByDimensionCustomerForecast: [],
    inventoryDashboardByDimensionForecastAccuracy: [],
    bulkEditAggregateBusiness: [],
    bulkEditBaseBusiness: [],
    editMasterAggregateBusiness: [],
    editMasterBaseBusiness: [],
  };

  // 並び替えができるヘッダーの集合
  public sortableBusinessTableHeaders = (type: string): boolean => {
    if (
      type === 'endUser' ||
      type === 'salesDestinationName' ||
      type === 'salesStatus' ||
      type === 'parentBusinessNumber' ||
      type === 'parentBusinessName' ||
      type === 'parentUnit' ||
      type === 'lossRate' ||
      type === 'safetyNumber' ||
      type === 'factoryPlace' ||
      type === 'manufacturingLine' ||
      type === 'parentNote' ||
      type === 'childBusinessNumber' ||
      type === 'childBusinessName' ||
      type === 'childUnit' ||
      type === 'supplierName' ||
      type === 'partsWeight' ||
      type === 'deliveryDestinationName' ||
      type === 'businessName' ||
      type === 'businessNumber' ||
      type === 'note' ||
      type === 'unit' ||
      type === 'form' ||
      type === 'leadTime' ||
      type === 'MOQ' ||
      type === 'stdInventoryIndex' ||
      type === 'orderAmount' ||
      type === 'recommendedOrderAmount' ||
      type === 'inventory' ||
      type === 'senderName' ||
      type === 'po' ||
      type === 'fixOrderDate' ||
      type === 'deliveryDate' ||
      type === 'orderDate' ||
      type === 'businessNumberNamedByUser' ||
      type === 'package' ||
      type === 'customerName' ||
      type === 'warehouse' ||
      type === 'manufacture' ||
      type === 'executionDate'
    ) {
      return true;
    } else {
      return false;
    }
  };

  // 数量情報のプロパティ群
  public commonDisplayAmountAndDateContents = (type: string): boolean => {
    if (type === 'package') {
      return true;
    } else {
      return false;
    }
  };

  // まとめられそうなBOMプロパティ群
  public commonDisplayBomContents = (type: string): boolean => {
    if (type === 'lossRate' || type === 'safetyNumber' || type === 'factoryPlace' || type === 'manufacturingLine') {
      return true;
    } else {
      return false;
    }
  };
}

/**
 * ユーザーが任意に追加したカラムの表示内容を制御する
 */
export class VariableTableContentViewModel extends ViewModel {
  public getVariableTableHeader = (tableProperty: TableProperty): string => {
    if (tableProperty.customFormulaTitle) {
      return tableProperty.customFormulaTitle;
    } else {
      return '';
    }
  };

  public getVariableTableContent = (tableProperty: TableProperty, item: IBusiness): string => {
    const customFormula = simpleCopy(tableProperty.customFormula) || [];
    // 数字以外の場合、該当する数値をitemから取得して、置き換える
    for (const [index, term] of Object.entries(customFormula)) {
      // そのままいけるやつはそのままいく
      if (item?.[term]) {
        customFormula[index] = item?.[term];
      } else if (typeof term !== 'number' && typeof term === 'string' && term.length > 1) {
        // そのままいけないやつは、関数にかける(算術記号以外の文字列が含まれている場合)
        const res = getValueForAdditionalHeaders(term, item);
        if (res.type === 'success') {
          customFormula[index] = res.value;
        } else {
          return 'error';
        }
      }
    }

    // 数式を評価して、結果を返す
    const evaluateFormula = (): Result<any, Error> => {
      try {
        const result = eval(customFormula.join(' ')); // TODO: evalを使うのは危険なので、後で変更する
        return {
          type: 'success',
          value: result,
        };
      } catch (e) {
        return {
          type: 'error',
          error: new Error('計算に失敗しました。'),
        };
      }
    };

    const result = evaluateFormula();
    if (result.type === 'success') {
      return String(result.value) ?? ' ';
    } else {
      return ' ';
    }
  };
}
